import React, { useState, createContext, useContext } from 'react';
import { useCart } from '../context/CartContext';
import { useOrder } from '../context/OrderContext';
import { createOrderSession, placeOrder } from '../services/api';

// Create a context for the OrderManager
const OrderManagerContext = createContext();

// Hook to use the OrderManager
export const useOrderManager = () => useContext(OrderManagerContext);

// Provider component that wraps the app
export const OrderManagerProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [orderData, setOrderData] = useState(null);
  
  const { cartItems, clearCart } = useCart();
  const { setOrderPlaced } = useOrder();

  // Handle checkout process
  const handlePlaceOrder = async (onSuccess) => {
    if (cartItems.length === 0) {
      setError('Your cart is empty');
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Create a copy of the current cart items to preserve descriptions
      const currentItems = cartItems.map(item => ({...item}));
      
      // Step 1: Create order session
      const sessionResponse = await createOrderSession();
      if (!sessionResponse || !sessionResponse.order || !sessionResponse.order.userSessionId) {
        throw new Error('Failed to create order session');
      }
      
      const userSessionId = sessionResponse.order.userSessionId;
      
      // Step 2: Place the order
      const orderResponse = await placeOrder(userSessionId, cartItems);
      
      if (!orderResponse || orderResponse.Result !== 0) {
        throw new Error(orderResponse?.ErrorDescription || 'Failed to place order');
      }
      
      // Create order data object
      const orderDataObj = {
        userSessionId: userSessionId,
        originalItems: currentItems,
        timestamp: Date.now()
      };
      
      // Store in state
      setOrderData(orderDataObj);
      
      // Store in localStorage for persistence across page navigation
      // Use a consistent key name that the PaymentPage will check
      localStorage.setItem('cinemaSnacksOrderData', JSON.stringify(orderDataObj));
      
      // Also store a simple flag to indicate an order is in progress
      // This is a more reliable way for the payment page to check
      sessionStorage.setItem('hasActiveOrder', 'true');
      
      // Mark the order as valid in the OrderContext
      setOrderPlaced(userSessionId);
      
      // Show success modal immediately
      setShowSuccessModal(true);
      
      // Clear the cart after successful order
      clearCart();
      
      // Call the success callback if provided - AFTER everything else is done
      // This ensures the order success modal is already visible when the cart modal closes
      if (onSuccess && typeof onSuccess === 'function') {
        setTimeout(() => {
          onSuccess();
        }, 100); // Small delay to ensure smooth transition
      }
      
      return true;
    } catch (err) {
      setError(err.message || 'An error occurred during checkout');
      console.error('Checkout error:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Create the context value
  const contextValue = {
    isLoading,
    setIsLoading, // Expose setIsLoading to consumers
    error,
    handlePlaceOrder,
    showSuccessModal,
    setShowSuccessModal,
    orderData
  };

  // We'll only render the OrderSuccessModal if we're inside a Router context
  // This prevents the useNavigate() error
  return (
    <OrderManagerContext.Provider value={contextValue}>
      {children}
    </OrderManagerContext.Provider>
  );
};

// For backward compatibility
const OrderManager = ({ children }) => {
  return <OrderManagerProvider>{children}</OrderManagerProvider>;
};

export default OrderManager;
